import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Landmark, ArrowRight, Loader2 } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { formatCurrency } from "@/lib/format";
import api from "@/lib/api";
import { TenXBrandFooter } from "@/components/TenXBrandFooter";

export default function LpInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/lp-invites/${token}`)
      .then(({ data }) => setInvite(data))
      .catch((e) => setError(e?.response?.data?.detail || "This invitation link is invalid."));
  }, [token]);

  const proceed = (path) => {
    sessionStorage.setItem("tenx_post_auth_redirect", `/portal/funds/${invite.lp_id}`);
    navigate(path);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6 py-12">
      <div className="w-full max-w-md">
        <div className="flex justify-center"><Logo /></div>
        {error && (
          <Card className="mt-8 rounded-2xl border p-8 text-center" data-testid="lp-invite-error">
            <p className="text-sm text-muted-foreground">{error}</p>
            <Button variant="outline" className="mt-4" onClick={() => navigate("/")}>Go to tenx</Button>
          </Card>
        )}
        {!error && !invite && <p className="mt-10 flex justify-center text-sm text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading your invitation…</p>}
        {invite && (
          <Card className="mt-8 rounded-2xl border p-8" data-testid="lp-invite-card">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary"><Landmark className="h-6 w-6" /></div>
            <h1 className="h-display mt-4 text-2xl font-semibold text-foreground">You're a Limited Partner in {invite.fund_name}</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {invite.gp_name} added <span className="font-medium text-foreground">{invite.lp_name}</span> ({invite.email}) as an LP
              {invite.commitment ? <> with a commitment of <span className="font-semibold text-foreground">{formatCurrency(invite.commitment)}</span></> : null}.
            </p>
            <div className="mt-4 rounded-lg bg-secondary/60 p-3 text-xs text-muted-foreground">
              Your LP portal shows your capital account, open capital calls, distributions and fund documents.
            </div>
            {invite.has_account ? (
              <>
                <Button className="mt-6 w-full gap-1.5" onClick={() => proceed("/login")} data-testid="lp-invite-signin">
                  Sign in to view your position <ArrowRight className="h-4 w-4" />
                </Button>
                <p className="mt-3 text-center text-xs text-muted-foreground">Use your existing tenx account ({invite.email}).</p>
              </>
            ) : (
              <>
                <Button className="mt-6 w-full gap-1.5" onClick={() => proceed(`/register?role=investor&email=${encodeURIComponent(invite.email)}&invite=${encodeURIComponent(token)}`)} data-testid="lp-invite-register">
                  Create your LP account <ArrowRight className="h-4 w-4" />
                </Button>
                <button className="mt-3 w-full text-center text-xs font-medium text-primary hover:underline" onClick={() => proceed("/login")} data-testid="lp-invite-signin-alt">
                  Already have an account? Sign in
                </button>
              </>
            )}
          </Card>
        )}
        <TenXBrandFooter context="lp-invite" className="mt-6" />
      </div>
    </div>
  );
}
